import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { inputClass } from './Field'

/**
 * A text input that offers matching options as you type, in a list exactly
 * as wide as the input and directly under it (SM-21). Anything can still be
 * typed: the options only help.
 *
 * In place of a <datalist>, whose list the browser draws however it likes —
 * on Chrome a centred popup of its own width, ordered by its own rules.
 *
 * Draws its own label: the list can't sit inside a <label> (as Field puts its
 * control), or every option would be read out as part of the field's name.
 */

/** Most options shown at once; the list scrolls, but hundreds of rows help no one. */
const MAX_SHOWN = 100

const collator = new Intl.Collator('en-GB', { sensitivity: 'base' })

/** Options containing the typed text, alphabetical, each once. */
export function matchingOptions(options: string[], typed: string): string[] {
  const wanted = typed.trim().toLowerCase()
  const seen = new Set<string>()
  return options
    .filter((o) => {
      const key = o.toLowerCase()
      if (seen.has(key) || (wanted !== '' && !key.includes(wanted))) return false
      seen.add(key)
      return true
    })
    .sort(collator.compare)
}

interface Props {
  label: string
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder?: string
  required?: boolean
  autoFocus?: boolean
  maxLength?: number
  /** Around the label and input; defaults to a form row's spacing. */
  className?: string
  labelClassName?: string
}

export function Combobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
  autoFocus,
  maxLength,
  className = 'mb-4',
  labelClassName = 'block text-xs font-sans text-ink/75 mb-1',
}: Props) {
  const inputId = useId()
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)

  const shown = useMemo(() => matchingOptions(options, value).slice(0, MAX_SHOWN), [options, value])
  // Nothing to offer once the input already says exactly the only match.
  const exact = shown.length === 1 && shown[0].toLowerCase() === value.trim().toLowerCase()
  const expanded = open && shown.length > 0 && !exact

  // In a dialog that scrolls, a field near the bottom would open its list out
  // of sight: bring it into view as it opens.
  const listRef = useRef<HTMLUListElement>(null)
  useEffect(() => {
    if (expanded) listRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [expanded])

  function pick(option: string) {
    onChange(option)
    setOpen(false)
    setActive(-1)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!expanded) {
        setOpen(true)
        setActive(e.key === 'ArrowDown' ? 0 : shown.length - 1)
        return
      }
      const step = e.key === 'ArrowDown' ? 1 : -1
      setActive((i) => (i + step + shown.length) % shown.length)
    } else if (e.key === 'Enter' && expanded && active >= 0) {
      e.preventDefault() // picks the option rather than submitting the form
      pick(shown[active])
    } else if (e.key === 'Escape' && expanded) {
      e.stopPropagation() // closes the list, not the dialog around it
      setOpen(false)
      setActive(-1)
    }
  }

  return (
    <div className={className}>
      <label htmlFor={inputId} className={labelClassName}>
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          className={inputClass}
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
            setOpen(true)
            setActive(-1)
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          autoFocus={autoFocus}
          maxLength={maxLength}
        />
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          // Not named after the field: a label containing the field's ("Name
          // suggestions") also answers to a search for the field by its label.
          aria-label="Suggestions"
          hidden={!expanded}
          className="absolute left-0 right-0 top-full z-20 mt-0.5 max-h-56 overflow-y-auto bg-white border border-line rounded-sm shadow-md py-1"
        >
          {expanded &&
            shown.map((option, i) => (
              <li
                key={option}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                // mousedown, not click: a click would blur the input first and close the list.
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(option)
                }}
                onMouseEnter={() => setActive(i)}
                className={`px-2 py-1.5 text-sm cursor-pointer ${i === active ? 'bg-accent/10 text-ink' : 'text-ink/90'}`}
              >
                {option}
              </li>
            ))}
        </ul>
      </div>
    </div>
  )
}
